import { Check } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type Ref,
} from 'react'
import { flushSync } from 'react-dom'
import { showDate } from '../../../shared/tree'
import { albumPhotoUrl, photoName, photoSrcSet } from '@/lib/albums'
import { columnsFor, gapFor, masonry, tileImage, tileRatio, visible, type WallPhoto } from '@/lib/modern'
import { glowOf } from '@/lib/motion'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'

// One line of caption under a photo, and the gap above it, in pixels (styles/modern.css .mp-cap).
const CAPTION = 26
// How long a finger rests on a photo before it's held up to look at.
const HOLD = 420

export type WallHandle = {
  // Brings a photo's tile on screen (scrolling the page under whatever covers it) and returns its picture.
  reveal: (id: string) => HTMLImageElement | null
}

// The wall: every photo whole, in columns, each in turn under the shortest one so they read left to right in the
// order they came. Only the photos near the screen are drawn, so thousands scroll as smoothly as twenty. Photos rise
// into place the first time they're seen, sharpen as they arrive, and a touch presses them in. Held down on a phone,
// a photo lifts out over a blurred wall to look at; sliding up from there opens it.
export function Wall({
  photos,
  label,
  selected,
  onToggle,
  onOpen,
  more,
  onMore,
  smaller,
  feed,
  ref,
}: {
  photos: WallPhoto[]
  label: string
  // Smaller photos, one more column at every size (three on a phone).
  smaller?: boolean
  // One photo after another, each across the whole width, with room between them, like a feed.
  feed?: boolean
  selected?: Set<string>
  onToggle?: (id: string) => void
  onOpen: (id: string) => void
  more?: boolean
  onMore?: () => void
  ref?: Ref<WallHandle>
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [view, setView] = useState({ top: -1000, bottom: 3000 })
  const entered = useRef(new Set<string>())
  const [peek, setPeek] = useState<WallPhoto | null>(null)
  const peeking = useRef(false)
  const press = useRef<{ id: string; x: number; y: number; timer: number; held: boolean } | null>(null)
  const swallowClick = useRef(false)

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const watch = new ResizeObserver(([entry]) => setWidth(Math.round(entry!.contentRect.width)))
    watch.observe(el)
    setWidth(Math.round(el.getBoundingClientRect().width))
    return () => watch.disconnect()
  }, [])

  // Which stretch of the wall is near the screen, re-measured once a frame while scrolling, and only when it has
  // moved far enough to change what's drawn.
  const measure = useCallback(() => {
    const el = box.current
    if (!el) return
    const top = -el.getBoundingClientRect().top
    const next = { top: top - innerHeight, bottom: top + innerHeight * 2 }
    setView((v) => (Math.abs(v.top - next.top) < 150 && Math.abs(v.bottom - next.bottom) < 150 ? v : next))
  }, [])
  useLayoutEffect(() => {
    let frame = 0
    const onScroll = () => {
      if (!frame)
        frame = requestAnimationFrame(() => {
          frame = 0
          measure()
        })
    }
    measure()
    addEventListener('scroll', onScroll, { passive: true })
    addEventListener('resize', onScroll)
    return () => {
      removeEventListener('scroll', onScroll)
      removeEventListener('resize', onScroll)
      cancelAnimationFrame(frame)
    }
  }, [measure])

  // While a photo is held up, the page under it stays put.
  useEffect(() => {
    const hold = (e: TouchEvent) => {
      if (peeking.current) e.preventDefault()
    }
    addEventListener('touchmove', hold, { passive: false })
    return () => removeEventListener('touchmove', hold)
  }, [])

  const columns = feed ? 1 : columnsFor(width || 360, smaller)
  const gap = feed ? 16 : gapFor(width || 360)
  const extra = useMemo(() => photos.map((p) => (p.caption ? CAPTION : 0)), [photos])
  const layout = useMemo(
    () =>
      masonry(
        photos.map((p, i) => ({ ratio: tileRatio(p), extra: extra[i]! })),
        columns,
        width || 360,
        gap,
      ),
    [photos, extra, columns, width, gap],
  )
  const shown = visible(layout.boxes, extra, view.top, view.bottom)

  useLayoutEffect(() => measure(), [measure, layout.height])
  // More load as the end of the wall comes near.
  useEffect(() => {
    if (more && onMore && view.bottom > layout.height - innerHeight) onMore()
  }, [more, onMore, view.bottom, layout.height])

  useImperativeHandle(ref, () => ({
    reveal: (id) => {
      const i = photos.findIndex((p) => p.id === id)
      const el = box.current
      const b = layout.boxes[i]
      if (!el || !b) return null
      const top = el.getBoundingClientRect().top + scrollY + b.y
      if (top < scrollY + 64 || top + b.h > scrollY + innerHeight - 64) {
        scrollTo({ top: Math.max(0, top - innerHeight / 3), behavior: 'instant' })
        flushSync(measure)
      }
      return tileImage(id)
    },
  }))

  // Only a photo seen for the first time rises in, row by row down the screen; one drawn again after scrolling away
  // and back is just there. Nothing rises while the wall is rearranging itself, which moves photos its own way.
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const morphing = !!document.documentElement.dataset.morph
    const top = -el.getBoundingClientRect().top
    for (const tile of el.querySelectorAll<HTMLElement>('.mp-tile')) {
      const id = tile.dataset.photo!
      if (entered.current.has(id)) continue
      entered.current.add(id)
      if (morphing) continue
      const y = tile.offsetTop
      const delay = Math.min(Math.max(0, y - top) / 3, 420) + (tile.offsetLeft / Math.max(tile.offsetWidth, 1)) * 45
      tile.style.setProperty('--d', `${Math.round(delay)}ms`)
      tile.classList.add('mp-enter')
      tile.addEventListener('animationend', () => tile.classList.remove('mp-enter'), { once: true })
    }
  })

  const cancelPress = () => {
    const p = press.current
    if (p) clearTimeout(p.timer)
    if (p?.held) {
      swallowClick.current = true
      peeking.current = false
      setPeek(null)
    }
    press.current = null
  }
  const hold = (photo: WallPhoto) => ({
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType !== 'touch' || onToggle) return
      swallowClick.current = false
      const timer = window.setTimeout(() => {
        if (!press.current) return
        press.current.held = true
        peeking.current = true
        navigator.vibrate?.(8)
        setPeek(photo)
      }, HOLD)
      press.current = { id: photo.id, x: e.clientX, y: e.clientY, timer, held: false }
    },
    onPointerMove: (e: ReactPointerEvent) => {
      const p = press.current
      if (!p) return
      if (!p.held && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) return cancelPress()
      if (p.held && p.y - e.clientY > 70) {
        clearTimeout(p.timer)
        press.current = null
        peeking.current = false
        swallowClick.current = true
        setPeek(null)
        onOpen(photo.id)
      }
    },
    onPointerUp: cancelPress,
    onPointerCancel: cancelPress,
    onContextMenu: (e: { preventDefault: () => void }) => e.preventDefault(),
  })

  return (
    <>
      <div
        ref={box}
        className="mp-wall"
        data-smaller={smaller ? '' : undefined}
        style={{ height: layout.height }}
        role="list"
        aria-label={label}
      >
        {shown.map((i) => {
          const photo = photos[i]!
          const b = layout.boxes[i]!
          const name = photoName(photo, i + 1)
          const style = { left: b.x, top: b.y, width: b.w, '--vt': `mp-${photo.id}` } as CSSProperties
          const picture = (
            <span className={cn('mp-frame', tint(i))} style={{ height: b.h }}>
              <img
                src={albumPhotoUrl(photo.ref, 'thumb')}
                srcSet={photoSrcSet(photo, ['thumb', 'medium'])}
                sizes={`${Math.round(b.w)}px`}
                alt={onToggle ? '' : name}
                decoding="async"
                draggable={false}
                className="mp-img"
                ref={(img) => {
                  if (img?.complete && img.naturalWidth && !img.dataset.loaded) img.dataset.loaded = 'instant'
                }}
                onLoad={(e) => {
                  const img = e.currentTarget
                  if (!img.dataset.loaded) img.dataset.loaded = 'true'
                  const glow = glowOf(photo.id, img)
                  if (glow) img.closest<HTMLElement>('.mp-tile')?.style.setProperty('--glow', glow)
                }}
              />
              {onToggle && (
                <span className="mp-check" aria-hidden>
                  <Check className="size-3.5" strokeWidth={3} />
                </span>
              )}
            </span>
          )
          const caption = photo.caption && <span className="mp-cap">{photo.caption}</span>
          return (
            <div key={photo.id} role="listitem">
              {onToggle ? (
                <button
                  type="button"
                  data-photo={photo.id}
                  aria-pressed={selected?.has(photo.id) ?? false}
                  aria-label={name}
                  className="mp-tile"
                  style={style}
                  onClick={() => onToggle(photo.id)}
                >
                  {picture}
                  {caption}
                </button>
              ) : (
                <a
                  href={`/ph/${photo.id}`}
                  data-photo={photo.id}
                  className="mp-tile"
                  style={style}
                  onClick={(e) => {
                    // A new tab or window still gets the photo's own page.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                    e.preventDefault()
                    if (swallowClick.current) {
                      swallowClick.current = false
                      return
                    }
                    onOpen(photo.id)
                  }}
                  {...hold(photo)}
                >
                  {picture}
                  {caption}
                </a>
              )}
            </div>
          )
        })}
      </div>
      {peek && <Peek photo={peek} />}
    </>
  )
}

// A photo held up to look at, over the blurred wall, until the finger lifts.
function Peek({ photo }: { photo: WallPhoto }) {
  const glow = glowOf(photo.id, null)
  return (
    <div className="mp-peek" style={glow ? ({ '--glow': glow } as CSSProperties) : undefined} aria-hidden>
      <div className="mp-peek-card">
        <img
          src={albumPhotoUrl(photo.ref, 'thumb')}
          srcSet={photoSrcSet(photo, ['thumb', 'medium'])}
          sizes="26rem"
          alt=""
          className="block w-full"
          style={{ aspectRatio: String(tileRatio(photo)), objectFit: 'cover' }}
        />
        <div className="flex flex-col gap-0.5 px-4 py-3">
          {photo.caption && <p className="font-medium">{photo.caption}</p>}
          <p className="text-sub text-muted-foreground">
            {photo.takenOn ? showDate(photo.takenOn) : 'Date not known'} · Slide up to open
          </p>
        </div>
      </div>
    </div>
  )
}
